import React, { useState } from 'react';
import "../../styles/pages/help/Help.css";

const Help = () => {
  const [openSectionIndex, setOpenSectionIndex] = useState(null);
  const [openQuestionIndex, setOpenQuestionIndex] = useState(null);

  const sections = [
    {
      title: "Login Page",
      questions: [
        {
          question: "How do I log in to FinePunch ERP?",
          answer:
            "On the login page, enter your registered username in the first field, and your password in the second field. Then click the 'Login' button to access the system."
        },
        {
          question: "What are the required fields for login?",
          answer:
            "You must enter a valid mobile number or username, and the correct password. Both fields are mandatory."
        },
        {
          question: "I entered my username and password, but it says 'Invalid Credentials'. What should I do?",
          answer:
            "Make sure you're entering the correct username and password. Check for spelling, extra spaces, or caps lock. If you forgot your password, use the 'Forgot Password' option to reset it."
        }
      ]
    },
    {
      title: "Filters & Search",
      questions: [
        {
          question: "How can I filter by Operation and Category?",
          answer:
            "You can use the filter options available at the top of the list or table. Select the desired Operation and Category from the dropdown menus to filter the results accordingly."
        },
        {
          question: "How do I clear all filters?",
          answer:
            "Click the 'Clear All' button near the filters section. This will reset all selected filters like Operation, Category, Date, etc., and display the full list again."
        }
      ]
    },

    {
      title: "Master",
      questions: [
        {
          question: "How do I create a new customer in Customer Master?",
          answer:
            "Go to Master > Customer > Add Customer. Fill in all required details such as name, contact number, address, and GST number (if applicable), then click 'Save'."
        },
        {
          question: "How can I view or edit customer details?",
          answer:
            "Navigate to Master > Customer. You will see a list of all customers. Click the 'View' icon to see details, or click the 'Edit' icon to update and save customer information."
        },
        {
          question: "How do I add a customer in Jobwork or Prototype?",
          answer:
            "While creating a Jobwork or Prototype entry, you can select an existing customer from the dropdown or add a new customer by clicking the 'Add Customer' button and entering their details."
        },
        {
          question: "How can I view customer details in Jobwork or Prototype?",
          answer:
            "After selecting a customer in the Jobwork or Prototype form, click the 'View' icon next to the dropdown to see the full customer details like contact info and address."
        }
      ]
    },
    {
      title: "Marketing",
      questions: [
        {
          question: "How do I add a new sales enquiry in Marketing?",
          answer:
            "Go to Marketing > Enquiry > Add New. Fill in the enquiry details such as customer, product, and remarks, then click 'Save' to create a new sales enquiry."
        },
        {
          question: "How can I view, edit, or delete existing sales enquiries?",
          answer:
            "Navigate to Marketing > Enquiry. You'll see a list of all enquiries. Use the 'View', 'Edit', or 'Delete' icons next to each row to manage them."
        },
        {
          question: "How do I add and manage sales quotations?",
          answer:
            "Go to Marketing > Quotation > Add New to create a sales quotation. You can also view or delete existing quotations from the quotation list screen."
        },
        {
          question: "How do I add and view a customer purchase order (Customer PO)?",
          answer:
            "Go to Marketing > Customer PO > Add New. Fill out the sales order form and click 'Save'. You can view existing orders from the list view."
        },
        {
          question: "How do I create, edit, or delete a Work Order?",
          answer:
            "Navigate to Marketing > Work Order > Add New to create a new work order. You can also view, edit, or delete work orders from the listing screen using the respective action icons."
        },
        {
          question: "How do I create a Material Request from a Work Order?",
          answer:
            "While creating or editing a Work Order, you can generate a Material Request by selecting items and clicking on 'Create Material Request'. The request will be linked to the Work Order."
        }
      ]
    },
    {
      title: "Quality",
      questions: [
        {
          question: "How do I add and view GRN Incoming Inspection details?",
          answer:
            "Go to Quality > GRN Incoming Inspection > Add New. Enter the inspection parameters, remarks, and status. Click 'Save'. You can view existing inspections from the list screen."
        },
        {
          question: "How do I manage Product Incoming Inspection?",
          answer:
            "Navigate to Quality > Product Incoming Inspection > Add New. Fill in the inspection information, check values, and comments. Click 'Save'. You can also view inspection history from the list."
        },
        {
          question: "How do I add and view First Piece Inspection?",
          answer:
            "Go to Quality > First Piece Inspection > Add New. Enter inspection results for the first piece produced. After saving, you can view or track results from the list view."
        },
        {
          question: "How do I record In-Process Inspection details?",
          answer:
            "Navigate to Quality > In-Process Inspection > Add New. Fill in process-stage-specific checks, measurements, and inspector remarks. You can view existing entries from the same section."
        },
        {
          question: "How do I manage Product Final Inspection?",
          answer:
            "Go to Quality > Product Final Inspection > Add New. Enter final check values, pass/fail status, and comments. Saved entries can be viewed or reviewed later from the inspection list."
        }
      ]
    },
    {
      title: "Inventory",
      questions: [
        {
          question: "How do I add and view a Material Request?",
          answer:
            "Go to Inventory > Material Request > Add New to create a new request. Fill in the required materials, quantity, and department. Click 'Save'. You can view all material requests in the listing screen."
        },
        {
          question: "How do I create a new Purchase Indent?",
          answer:
            "Navigate to Inventory > Purchase Indent > Add New. Enter the indent details including item name, quantity, and required date. Then click 'Save' to submit the indent."
        },
        {
          question: "How can I add and view Opening Balance?",
          answer:
            "Go to Inventory > Opening Balance > Add New. Select the item, enter available quantity and rate, then save. You can view all previously entered opening balances in the list view."
        }
      ]
    },
    {
      title: "Accounts",
      questions: [
        {
          question: "How do I add a new Credit Note?",
          answer:
            "Go to Accounts > Credit Note > Add New. Enter the customer, invoice reference, credit amount, and reason. Click 'Save' to generate the credit note."
        },
        {
          question: "How do I add a new Debit Note?",
          answer:
            "Navigate to Accounts > Debit Note > Add New. Fill in the vendor, reference number, debit amount, and remarks. Click 'Save' to create the debit note."
        }
      ]
    },
    {
      title: "Reports",
      questions: [
        {
          question: "How do I view sales-related reports like Quotation, Order, and Register?",
          answer:
            "Go to Reports > Sales. From there, you can select 'Sales Quotation Register', 'Sales Order Register', or 'Sales Register'. Use the date filter to view records within a specific time range."
        },
        {
          question: "How do I access purchase-related reports?",
          answer:
            "Navigate to Reports > Purchase. You can view 'Purchase Quotation Register', 'Purchase Order Register', or 'Purchase Register'. Use the date filter to narrow down your report results."
        }
      ]
    },
    {
      title: "Graphic Reports",
      questions: [
        {
          question: "How can I view Sales and Purchase Graphic Reports?",
          answer:
            "Go to Reports > Graphic Reports. You can choose between 'Sales' or 'Purchase' reports. Select the type of report you want to see, apply the desired date range, and click 'Filter' to view the data in graphical format."
        },
        {
          question: "Can I filter Graphic Reports by date and type?",
          answer:
            "Yes. Graphic Reports allow you to filter by report type (e.g., Monthly Sales, Purchase Trend) and date range. The filtered data will be shown as a visual chart or graph."
        }
      ]
    },
    {
      title: "Approvals",
      questions: [
        {
          question: "How do I approve or reject a Sales Order?",
          answer:
            "Go to Approvals > Sales Order. You can see a list of sales orders with their current status (Pending, Approved, or Rejected). Click on 'Approve' or 'Reject' to take action on a specific order."
        },
        {
          question: "How do I approve or reject a Purchase Order?",
          answer:
            "Navigate to Approvals > Purchase Order. You will see the list of pending orders. Click the 'Approve' or 'Reject' button next to each order to update its status."
        },
        {
          question: "Where can I see the approval status of Sales or Purchase Orders?",
          answer:
            "In the respective approval sections, each order will show its current status (Pending, Approved, or Rejected) clearly next to the action buttons."
        }
      ]
    },
    {
      title: "Settings",
      questions: [
        {
          question: "How do I manage menu items in Settings?",
          answer:
            "Go to Settings > Menu Management. You can view all menu items listed there. Use the toggle switch to activate or deactivate any menu item as needed."
        },
        {
          question: "Can I reorder menu items?",
          answer:
            "Yes. You can drag and drop menu items to reorder them. Simply click and hold the item, drag it to the desired position, and release. After reordering, click 'Save' to apply the changes."
        }
      ]
    },
    {
      title: "User Account",
      questions: [
        {
          question: "What happens when I click Logout?",
          answer:
            "When you click the Logout button, a confirmation popup will appear asking: 'Are you sure you want to logout?'. If you click 'Yes', you will be logged out and redirected to the login page. If you click 'No', you will stay on the current page."
        }
      ]
    },
    {
      title: "Footer",
      questions: [
        {
          question: "What happens when I click on 'User Manual' in the footer?",
          answer:
            "Clicking on 'User Manual' opens a dedicated User Manual page where the PDF is shown. Use the Download PDF button at the top of that page to download the manual file."
        },
        {
          question: "What is shown when I click on 'Shortcut Keys' in the footer?",
          answer:
            "Clicking 'Shortcut Keys' will open a popup or section showing a list of keyboard shortcuts available in the system, helping you navigate and perform actions faster."
        },
        {
          question: "Where can I view the Terms and Conditions?",
          answer:
            "Clicking on 'Terms and Conditions' in the footer will open a page displaying the full terms and conditions for using the FinePunch ERP platform."
        }
      ]
    },
    {
      title: "General",
      questions: [
        {
          question: "What can I do on the Help page?",
          answer:
            "The Help page displays frequently asked questions (FAQs) grouped by topic. You can click on each question to expand and view its answer. It helps users understand how to use different modules of the system."
        },
        {
          question: "How can I edit my profile information?",
          answer:
            "Go to the Profile page by clicking the Profile icon in the top right corner. You can update your details such as name, email, contact number, and click 'Save' to apply the changes."
        }
      ]
    },
    {
      title: "Purchase",
      questions: [
        {
          question: "How do I add a new Purchase Enquiry?",
          answer:
            "Go to Purchase > Enquiry > Add New. Select a product category from the dropdown (Electrical, Mechanical, Others, Scrap, Sheet), fill in the enquiry details, and select or add a customer. Then click 'Save'."
        },
        {
          question: "Can I view or edit existing Purchase Enquiries?",
          answer:
            "Yes. Navigate to Purchase > Enquiry. You can view enquiry details or click the edit icon to modify them."
        },
        {
          question: "How do I create a Purchase Quotation?",
          answer:
            "Go to Purchase > Quotation > Add New. Select the company name, enter quotation details like item, price, and quantity, and click 'Save'."
        },
        {
          question: "How do I add and manage Purchase Orders?",
          answer:
            "Go to Purchase > Order > Add New. Enter purchase details including supplier, items, and delivery date. You can also view or edit existing purchase orders from the listing."
        },
        {
          question: "How do I add purchase details in the Purchase section?",
          answer:
            "Under Purchase > Purchase, you can record purchase transactions by entering supplier and product details, invoice number, and amounts. Click 'Save' to complete."
        },
        {
          question: "How do I add and view Goods Receipt Note (GRN)?",
          answer:
            "Go to Purchase > GRN > Add New. Fill in received goods details like quantity and condition. After saving, you can view the GRN entry from the list."
        },
        {
          question: "How do I add a Purchase Return?",
          answer:
            "Navigate to Purchase > Return > Add New. Select the items being returned, mention the reason and quantity, then click 'Save' to submit the return record."
        }
      ]
    },
    {
      title: "Production",
      questions: [
        {
          question: "How do I add new Input Production?",
          answer:
            "Go to Production > Input Production > Add New. Enter required details such as Work Order, Item, Quantity, and Date, then click 'Save' to submit the production input."
        },
        {
          question: "How do I add Output Production?",
          answer:
            "Navigate to Production > Output Production > Add New. Fill in the produced quantity, batch number, and remarks, then click 'Save' to record the output."
        },
        {
          question: "How can I view, edit, or manage production entries?",
          answer:
            "To view or update production records, go to Production > Input Production or Output Production. Use the 'View' and 'Edit' icons next to each entry to manage them."
        }
      ]
    }

  ];

  const toggleSection = (index) => {
    setOpenSectionIndex(openSectionIndex === index ? null : index);
    setOpenQuestionIndex(null);
  };

  const toggleQuestion = (index) => {
    setOpenQuestionIndex(openQuestionIndex === index ? null : index);
  };

  return (
    <div className="help-container">
      <h1 className="help-title">Need Help?</h1>
      <p className="help-subtitle">Browse FAQs by section</p>

      <div className="help-list">
        {sections.map((section, secIndex) => (
          <div key={secIndex} className="help-section">
            <div className="help-section-title" onClick={() => toggleSection(secIndex)}>
              {section.title}
              <span className="help-arrow">{openSectionIndex === secIndex ? "▲" : "▼"}</span>
            </div>

            {openSectionIndex === secIndex &&
              section.questions.map((item, qIndex) => (
                <div key={qIndex} className="help-item">
                  <div
                    className="help-question"
                    onClick={() => toggleQuestion(qIndex)}
                  >
                    {item.question}
                    <span className="help-arrow">
                      {openQuestionIndex === qIndex ? "▲" : "▼"}
                    </span>
                  </div>
                  {openQuestionIndex === qIndex && (
                    <div className="help-answer">{item.answer}</div>
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Help;